import { Component } from "react";
import { connect } from "react-redux";
import { Form, type InjectedFormProps, reduxForm, SubmissionError } from "redux-form";
import { type ConfigProps } from "redux-form/lib/reduxForm.js";

import {
  createAttributeValueInputs,
  getAttributeEncodedValue,
  getRawAttributeValue,
  getUniqueAttributeId,
} from "../../app-utils/sample.js";
import { capitalizeFirstLetter } from "../../app-utils/string.js";
import {
  type Attribute,
  type AttributeValue,
  type InputAttributeValueInput,
  type InputEditParentInput,
  type Parent,
} from "../../graphql.js";
import { checkParentNameUniqueness, saveEditedParent } from "../../redux/modules/parent.js";
import { type IAppState } from "../../redux/reducer.js";
import { ATTRIBUTE_TYPE, REPRESENTER_COLUMN } from "../../settings/settings.js";
import AttributeInputField from "./forms/AttributeInputField.js";
import { Dialog } from "./overlays/Dialogs.js";

export interface IParentEditingDialogOwnProps {
  viewType: string;
  summaryPageEntity: { id: number; groupType: string };
  close?: () => void;
}

interface IParentEditingDialogAppStateProps extends Partial<ConfigProps> {
  parent: Parent;
  parentAttributes: Attribute[];
}

interface IParentEditingDialogDispatchProps {
  saveParent: (editParentInput: InputEditParentInput) => Promise<Parent>;
  checkParentNameUniqueness: (name: string, parentId: number) => Promise<boolean>;
}

export interface IParentEditingDialogProps
  extends IParentEditingDialogAppStateProps,
    IParentEditingDialogDispatchProps,
    IParentEditingDialogOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState, _ownProps: IParentEditingDialogProps): IParentEditingDialogAppStateProps {
  const parent: Parent = state.parent.parent;
  const attributeDefaults = state.parent.attributeDefaults;

  // do not handle automatic, non-modifiable or derived term attributes (there are some non-automatic ones...),
  // neither in the form value init, nor in the display or when sending the data to the endpoint.
  // We should also filter-out multi-value parent attributes here because no suitable widget exists for editing.
  // Question: Should we also filter out the ones with template?
  const parentAttributes: Attribute[] = parent.parentType.parentAttributes.filter(
    (attribute) =>
      !attribute.automatic &&
      !attribute.multipleValues &&
      attribute.modifiable &&
      // edit_once can't be checked here easily because we would have to extract the current value beforehand to check if it is present
      // Therefore the input is disabled if required
      attribute.attributeType.id !== ATTRIBUTE_TYPE.DERIVED_TERMS &&
      // filter out templated attributes (wil be calculated on MT), except the parent name which should always be editable
      (attribute.representedColumn === REPRESENTER_COLUMN.NAME ||
        !attributeDefaults.find((d) => d.attributeId === attribute.id && d.data.template)),
  );

  const initialValues = {} as Record<string, unknown>;

  parent.attributeValues.forEach((attributeValue: AttributeValue) => {
    // figure out the experiment level attribute from the attribute value's attribute
    const experimentAttribute = parentAttributes.find((attribute) => attributeValue.attribute.id === attribute.id);
    if (experimentAttribute) {
      // build the initial entry as parent attribute value
      initialValues[getUniqueAttributeId(experimentAttribute)] = getAttributeEncodedValue(
        experimentAttribute,
        getRawAttributeValue(attributeValue),
      );
    }
  });
  // manually add the name which is not included in the attribute values (but only if the name attribute is not automatic)
  const nameAttribute: Attribute = parentAttributes.find((a) => a.representedColumn === REPRESENTER_COLUMN.NAME);
  let asyncBlurFields: string[] = [];
  if (nameAttribute) {
    const nameInputFormKey = getUniqueAttributeId(nameAttribute);
    initialValues[nameInputFormKey] = parent.name;
    asyncBlurFields = [nameInputFormKey];
  }

  return {
    asyncBlurFields: asyncBlurFields,
    parent: parent,
    parentAttributes: parentAttributes,
    initialValues,
  };
}

function mapDispatchToProps(dispatch): IParentEditingDialogDispatchProps {
  return {
    saveParent: (editParentInput: InputEditParentInput) => dispatch(saveEditedParent(editParentInput)),
    checkParentNameUniqueness: (name: string, parentId: number) => dispatch(checkParentNameUniqueness(name, parentId)),
  };
}

class ParentEditingDialog extends Component<IParentEditingDialogProps> {
  public static FORM_NAME = "parentEditing";

  handleSaveParent = (values): Promise<any> => {
    // see sample-editing.getSampleEditingInput
    const parentAttributeInput: InputAttributeValueInput[] = createAttributeValueInputs(
      this.props.parentAttributes,
      (attribute) => values[getUniqueAttributeId(attribute)],
    );

    const editParentInput: InputEditParentInput = {
      parentAttributes: parentAttributeInput,
      parentId: this.props.parent.id,
    };

    return this.props.saveParent(editParentInput).then(
      (fulfilled) => {
        // make sure the dialog is closed, in production mode, this does not work sometimes without the timeout
        setTimeout(() => {
          this.props.close && this.props.close();
          this.props.reset();
          this.props.destroy();
        }, 100);
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render() {
    const noEditableAttributes: boolean = this.props.parentAttributes.length === 0;

    return (
      <Dialog
        headerProps={{
          title: `Edit ${capitalizeFirstLetter(this.props.parent.parentType.name)}`,
          subtitle: this.props.parent.name,
        }}
        closeFn={this.props.close}
        mainActionButtonProps={{
          isLoading: this.props.submitting,
          isDisabled: this.props.submitting || noEditableAttributes,
          children: "Save",
          onPress: this.props.handleSubmit(this.handleSaveParent),
        }}
        footerMessage={{ message: this.props.error }}
      >
        <Form>
          {noEditableAttributes
            ? "There are no editable attributes in this parent type."
            : this.props.parentAttributes.map((attribute: Attribute) => (
                <AttributeInputField
                  attribute={attribute}
                  key={`attribute-${attribute.id}`}
                  verticalLayout
                  disabledByEditOnce={
                    attribute.editOnce &&
                    !!(this.props.initialValues && this.props.initialValues[getUniqueAttributeId(attribute)])
                  } // edit_once flag is set and there is already a value
                />
              ))}
        </Form>
      </Dialog>
    );
  }

  static async asyncValidate(...params: any[]) {
    const values = params[0];
    const props: IParentEditingDialogProps = params[2];
    const parentNameAttribute: Attribute = props.parent.parentType.parentAttributes.find(
      (a) => a.representedColumn === REPRESENTER_COLUMN.NAME,
    );
    const nameAttributeId: string = parentNameAttribute && getUniqueAttributeId(parentNameAttribute);
    const newParentName: string = values[nameAttributeId];

    // only validate if the name changed and if it was set in the redux state (it may be automatic!)
    if (newParentName && newParentName !== props.parent.name) {
      if (!(await props.checkParentNameUniqueness(newParentName, props.parent.id))) {
        throw {
          [nameAttributeId]: "Parent name is already in use in the database",
        };
      }
    }
  }
}

export default connect<
  IParentEditingDialogAppStateProps,
  IParentEditingDialogDispatchProps,
  IParentEditingDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: ParentEditingDialog.FORM_NAME,
    asyncValidate: ParentEditingDialog.asyncValidate,
  })(ParentEditingDialog) as any,
);

export { ParentEditingDialog };
